import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';

// Components
import ProfileAvatar from '../../ProfileAvatar';
import MotionModal from '../../modal/MotionModal';
import ProfileForm from './ProfileForm';
import AppToggle from '../../toggle/AppToggle';

// Services
import { getUserProfile, updateUserProfile } from './userProfileService';
import Loading from '../../loading/Loading';
import AlertMessage from '../../alert/AlertMessage';

// Define initial state outside to reuse it easily
const INITIAL_DATA = {
  marital_status: '',
  occupation: '',
  family_details: "",
  current_situation: '',
  personalized_prediction: "",
  user_notes: "",
};

const UserProfileModal = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [userProfileData, setUserProfileData] = useState(INITIAL_DATA);

  const [alert, setAlert] = useState({
    type: 'success',
    message: '',
  })

  const userId = user?._id;

  const resetAlert = () => setAlert({ type: 'success', message: '' })

  const handleClose = () => {
    setAlert({ type: 'success', message: '' })
    setIsOpen(false);
    setIsEditMode(false);
    setUserProfileData(INITIAL_DATA);
  };


  const handleUpdateUser = async (data) => {
    try {
      setUpdateLoading(true);
      if(alert.message) resetAlert();
      const payload = { user_id: userId, profile_identity: data }
      await updateUserProfile(userId, payload);
      setAlert({ type: 'success', message: 'User profile updated successfully' })
      setIsEditMode(false);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to update user profile";
      setAlert({ type: 'error', message: errorMessage })
    } finally {
      setUpdateLoading(false);
    }
  }


  // 1. Fetch data when userId exists AND when the modal opens
  useEffect(() => {
    if (!userId || !isOpen) return;
    const fetchUserProfile = async () => {
      setIsLoading(true);
      try {
        const response = await getUserProfile(userId);
        const data = response.data;
        setUserProfileData(data?.profile_identity);
        resetAlert()
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error.message || "Failed to fetch user profile";
        setAlert({ type: 'error', message: errorMessage })
      } finally {
        setIsLoading(false);
      }
    }

    fetchUserProfile();
  }, [userId, isOpen]);

  return (
    <>
      {!isOpen && (
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <ProfileAvatar size={40} onClick={() => setIsOpen(true)} />
        </motion.div>
      )}

      {isOpen && (
          <MotionModal
            className="min-w-6xl"
            open={isOpen}
            onClose={handleClose}
            title="User Profile"
            minWidth="60%"
          >
            {isLoading && <Loading text='Loading User Profile' />}
            {!isLoading &&
              <>
                <AlertMessage type={alert.type} message={alert.message} />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <AppToggle
                    checked={isEditMode}
                    onChange={(e) => setIsEditMode(e)}
                    labelOn="Edit"
                    labelOff="Edit"
                    disabled={isLoading || updateLoading}
                  />
                </Box>
                <ProfileForm
                  disabled={!isEditMode || isLoading}
                  data={userProfileData}
                  isEditMode={isEditMode}
                  onSubmit={handleUpdateUser}
                  isLoading={isLoading || updateLoading}
                  resetAlert={resetAlert}
                />
              </>
            }
          </MotionModal>
      )}
    </>
  )
}

export default UserProfileModal;